import React, { useEffect, useRef, useState } from 'react';
import { getCurrentListServer } from '../../utils/managerDns';

/**
 * Painel de diagnóstico compartilhado (Web e Stick).
 *
 * Mostra rede, latência ao servidor, memória, armazenamento, capas na tela e (quando houver) o vídeo.
 * No Stick, os dados do aparelho vêm do Android pela ponte AndroidBridge.getNativeDiagnostics()
 * (memória do aparelho/app, Wi-Fi, velocidade recebida, armazenamento). No Web, usa o que o navegador oferece.
 * Atualiza a cada 3s; a latência é medida a cada 10s — só enquanto o painel está aberto.
 */
interface DiagnosticsPanelProps {
  open: boolean;
  onClose?: () => void;
  videoElement?: HTMLVideoElement | null;
  /** Linhas extras de quem abriu (ex.: catálogo na memória, canal atual) */
  extraLines?: string[];
  /** Registros (ex.: eventos da reprodução no Web) — vão junto no "Copiar" */
  logs?: string[];
  title?: string;
  /** Dica de como fechar (Stick: tecla do controle) */
  closeHint?: string;
}

const mb = (bytes: number) =>
  bytes >= 1024 * 1024 * 1024 ? `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB` : `${Math.round(bytes / (1024 * 1024))} MB`;
const kbToMb = (kb: number) => (kb >= 0 ? `${Math.round(kb / 1024)} MB` : '?');

export const DiagnosticsPanel: React.FC<DiagnosticsPanelProps> = ({
  open,
  onClose,
  videoElement,
  extraLines = [],
  logs = [],
  title = 'Diagnóstico',
  closeHint,
}) => {
  const [lines, setLines] = useState<string[]>([]);
  const lastRx = useRef<{ bytes: number; at: number } | null>(null);
  const rxMbps = useRef<number>(0);
  const latency = useRef<number>(-1); // -1 medindo, -2 falhou
  const lastPingAt = useRef<number>(0);
  const storage = useRef<{ usage: number; quota: number } | null>(null);
  const lastStorageAt = useRef<number>(0);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    const ping = async () => {
      const t0 = Date.now();
      try {
        await fetch(`/favicon.svg?_t=${t0}`, { cache: 'no-store' });
        latency.current = Date.now() - t0; // qualquer resposta do servidor conta
      } catch {
        latency.current = -2;
      }
    };

    const collect = async () => {
      const out: string[] = [];
      const now = Date.now();
      let native: any = null;
      try {
        const raw = (window as any).AndroidBridge?.getNativeDiagnostics?.();
        native = raw ? JSON.parse(raw) : null;
      } catch {
        native = null;
      }

      // ── Rede ──
      if (native) {
        let rede = `Rede: ${native.netType || '?'}`;
        if (native.netType && native.netType !== 'sem conexao') {
          rede += native.validated ? ' | internet OK' : ' | internet NAO confirmada';
        }
        if (typeof native.wifiRssi === 'number') {
          rede += ` | sinal ${native.wifiRssi} dBm (${native.wifiLevel}/4)${native.wifiLevel <= 1 ? ' FRACO' : ''} | link ${native.wifiLinkMbps} Mbps`;
        }
        out.push(rede);
        if (typeof native.rxBytes === 'number') {
          if (lastRx.current && now > lastRx.current.at) {
            rxMbps.current = ((native.rxBytes - lastRx.current.bytes) * 8) / ((now - lastRx.current.at) / 1000) / 1_000_000;
          }
          lastRx.current = { bytes: native.rxBytes, at: now };
          out.push(`Velocidade: recebendo ${rxMbps.current.toFixed(1)} Mbps (tudo que o app baixa agora)`);
        }
      } else {
        const c = (navigator as any).connection;
        out.push(
          `Rede: ${navigator.onLine ? 'conectado' : 'SEM CONEXAO'}` +
            (c ? ` | tipo ${c.effectiveType || '?'} | estimada ${c.downlink ?? '?'} Mbps | rtt ${c.rtt ?? '?'} ms` : '')
        );
      }
      if (now - lastPingAt.current > 10000) {
        lastPingAt.current = now;
        ping();
      }
      out.push(
        `Latencia ao servidor: ${
          latency.current >= 0
            ? `${latency.current} ms${latency.current > 1500 ? ' (LENTA)' : ''}`
            : latency.current === -2
              ? 'FALHOU'
              : 'medindo...'
        }`
      );

      // ── Memória ──
      if (native && typeof native.memAvail === 'number') {
        const safeStr = typeof native.memSafe === 'number' ? ` (margem segura: ${mb(native.memSafe)})` : '';
        out.push(`Memoria do aparelho: livre ${mb(native.memAvail)}${safeStr} de ${mb(native.memTotal)}${native.memLow ? '  (BAIXA!)' : ''}`);
        if (typeof native.appPssKb === 'number') {
          out.push(
            `App: ${kbToMb(native.appPssKb)} (Java ${kbToMb(native.javaKb)}, nativo ${kbToMb(native.nativeKb)}, graficos ${kbToMb(native.graphicsKb)})`
          );
        }
      }
      const pm = (performance as any).memory;
      if (pm) out.push(`JavaScript: ${mb(pm.usedJSHeapSize)} de ${mb(pm.totalJSHeapSize)} (limite ${mb(pm.jsHeapSizeLimit)})`);
      if (native && typeof native.trimCount === 'number') {
        out.push(`Avisos de pouca memoria: ${native.trimCount}${native.lastTrimLevel >= 0 ? ` (ultimo nivel ${native.lastTrimLevel})` : ''}`);
      }

      // ── V8.2.9: eventos do player nativo (Stick) — sequência de abrir/fechar/troca de episódio ──
      // V9.0.4: servidor dos vídeos = a DNS da LISTA da conta (sempre 1; acabou o teste de velocidade)
      try {
        const dns = getCurrentListServer();
        const host = (() => { try { return new URL(dns).host; } catch { return dns; } })();
        out.push(`Servidor dos videos (DNS da lista): ${host || 'sem lista no aparelho (usa o endereco do proprio item)'}`);
      } catch {}
      if (native && typeof native.webViewRestarts === 'number') {
        out.push(`Tela do app reiniciada por falta de memoria: ${native.webViewRestarts} vez(es)${native.webViewRestarts > 0 ? '  (MEMORIA!)' : ''}`);
      }
      if (native && Array.isArray(native.playerEvents) && native.playerEvents.length > 0) {
        out.push('Eventos do player (mais recentes por ultimo):');
        native.playerEvents.slice(-10).forEach((e: string) => out.push(`  ${e}`));
      }

      // ── Armazenamento ──
      if (native && typeof native.storageFree === 'number') {
        out.push(
          `Armazenamento: livre ${mb(native.storageFree)} de ${mb(native.storageTotal)}${native.storageFree < 300 * 1024 * 1024 ? ' (POUCO!)' : ''}` +
            ` | app usa ${native.appDataBytes >= 0 ? mb(native.appDataBytes) : 'medindo...'}` +
            (native.appCacheBytes >= 0 ? ` (cache ${mb(native.appCacheBytes)})` : '')
        );
      }
      if (now - lastStorageAt.current > 10000) {
        lastStorageAt.current = now;
        try {
          const est = await (navigator as any).storage?.estimate?.();
          if (est) storage.current = { usage: est.usage || 0, quota: est.quota || 0 };
        } catch {}
      }
      if (storage.current) {
        out.push(`Dados guardados pelo app (catalogo, cache): ${mb(storage.current.usage)} de ${mb(storage.current.quota)} permitidos`);
      }

      // ── Tela: capas carregadas (o que mais cresce ao navegar) ──
      try {
        const imgs = Array.from(document.images);
        let loaded = 0;
        let pixels = 0;
        for (const img of imgs) {
          if (img.complete && img.naturalWidth > 0) {
            loaded++;
            pixels += img.naturalWidth * img.naturalHeight;
          }
        }
        out.push(
          `Tela: ${imgs.length} imagens (${loaded} carregadas, ~${mb(pixels * 4)} decodificadas) | ${document.getElementsByTagName('*').length} elementos`
        );
      } catch {}

      // ── Vídeo ──
      const v = videoElement;
      if (v && v.videoWidth) {
        let ahead = 0;
        try {
          for (let i = 0; i < v.buffered.length; i++) {
            if (v.buffered.start(i) <= v.currentTime && v.buffered.end(i) >= v.currentTime) ahead = v.buffered.end(i) - v.currentTime;
          }
        } catch {}
        const q = (v as any).getVideoPlaybackQuality ? (v as any).getVideoPlaybackQuality() : null;
        out.push(
          `Video: ${v.videoWidth}x${v.videoHeight} | buffer a frente ${ahead.toFixed(0)} s` +
            (q ? ` | quadros perdidos ${q.droppedVideoFrames} de ${q.totalVideoFrames}` : '')
        );
      }

      extraLines.forEach((l) => out.push(l));
      if (!cancelled) setLines(out);
    };

    collect();
    const t = setInterval(collect, 3000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, videoElement, extraLines.join('|')]);

  if (!open) return null;

  const copyAll = async () => {
    const text = [title, ...lines, ...(logs.length ? ['', 'Registros:', ...logs] : [])].join('\n');
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        ta.remove();
      } catch {}
    }
  };

  return (
    <div
      className="fixed top-2 left-2 right-2 sm:right-auto z-[12000] max-w-full sm:max-w-[760px] max-h-[90vh] overflow-y-auto rounded-xl bg-black/90 border border-white/15 text-white shadow-2xl"
      style={{ fontFamily: 'monospace' }}
    >
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-white/10">
        <span className="text-xs font-bold">{title}</span>
        {onClose && !closeHint && (
          <div className="flex gap-2">
            <button onClick={copyAll} className="px-2 py-1 rounded bg-white/10 hover:bg-white/20 text-[11px]">
              Copiar
            </button>
            <button onClick={onClose} className="px-2 py-1 rounded bg-white/10 hover:bg-white/20 text-[11px]">
              Fechar
            </button>
          </div>
        )}
        {closeHint && <span className="text-[10px] text-zinc-400">{closeHint}</span>}
      </div>
      <div className="px-3 py-2 text-[11px] sm:text-xs leading-relaxed">
        {lines.map((l, i) => (
          <div key={i} className="whitespace-pre-wrap break-words">
            {l}
          </div>
        ))}
        {logs.length > 0 && (
          <div className="mt-2 pt-2 border-t border-white/10 max-h-48 overflow-y-auto text-[10px] text-zinc-300">
            {logs.map((l, i) => (
              <div key={i} className="whitespace-pre-wrap break-all">
                {l}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
